import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../contexts/ThemeContext';
import LoadingSpinner from './LoadingSpinner';
import EnhancedLoadingState, { LoadingSkeleton } from './EnhancedLoadingState';
import WizardTransition, { FadeTransition, ScaleTransition } from './WizardTransition';
import EnhancedTooltip, { HelpTooltip } from './EnhancedTooltip';
import { ApiClient } from '../utils/apiClient';
import { getChatInterfaceClasses, getWizardLayoutClasses, getTouchFriendlyClasses, getResponsiveButtonClasses } from '../utils/responsiveUtils';

/**
 * Step 3 AI Configuration Component
 * Handles OpenAI chat interface for system prompt refinement
 */
const Step3AIConfig = ({
  formData = {},
  selectedTemplate = null,
  onComplete,
  onBack,
  onSaveAndExit,
  errors = {},
  isLoading = false,
  className='',
  isEditing = false,
  existingParrotId = null,
  isTransitioning = false,
  transitionDirection = 'forward'
}) => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const apiClient = useRef(new ApiClient(getToken)).current;
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Local state
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [chatStatus, setChatStatus] = useState('initializing'); // initializing, ready, sending, error, completed
  const [isInitializing, setIsInitializing] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [finalConfiguration, setFinalConfiguration] = useState(null);

  // Enhanced UX features
  const touchFriendlyClasses = getTouchFriendlyClasses();
  const layoutClasses = getWizardLayoutClasses();
  const chatClasses = getChatInterfaceClasses();

  // Initialize chat session on mount
  useEffect(() => {
    initializeChatSession();
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat is ready
  useEffect(() => {
    if (chatStatus === 'ready' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [chatStatus]);

  /**
   * Scroll to bottom of messages
   */
  const scrollToBottom = () => {
    if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /**
   * Initialize OpenAI chat session
   */
  const initializeChatSession = async () => {
    try {
      setIsInitializing(true);
      setChatError(null);
      setChatStatus('initializing');

      // The enhanced wizard expects a session_id
      const initData = {
        session_id: formData.session_id || 'temp-session-' + Date.now() // Temporary fallback
      };

      const response = await apiClient.post('/api/parrots/wizard/step3/start', initData);
      console.log('Step3 API response:', response);
      
      if (response && (response.conversation_id || response.fallback_mode)) {
        if (response.conversation_id) {
          setConversationId(response.conversation_id);
          
          // Set initial welcome message for successful chat initialization
          const welcomeMessage = response.ai_response || (isEditing 
            ? `Welcome back! I'm here to help you refine "${formData.name}". Your current first message is: "${formData.first_message}". 

I can help you adjust the system prompt to better match your needs. You can:
- Ask me to explain how your parrot currently behaves by typing "Overview"
- Request specific changes to the behavior
- Type "Finalize" when you're satisfied with the configuration

What would you like to do?`
            : `Great! Now let's create the perfect system prompt for "${formData.name}". 

Based on your selected template "${selectedTemplate?.name}" and first message "${formData.first_message}", I'll help you customize how your parrot should behave during calls.

You can:
- Ask me to explain the current behavior by typing "Overview"
- Request specific changes or improvements
- Type "Finalize" when you're happy with the configuration

What would you like to focus on first?`);

          setMessages([{
            id: 'welcome',
            type: 'assistant',
            content: welcomeMessage,
            timestamp: new Date().toISOString()
          }]);

          setChatStatus('ready');
        } else if (response.fallback_mode) {
          // Handle fallback mode - AI chat is unavailable
          setChatError(response.message || 'AI chat is temporarily unavailable. You can enter your system prompt manually or try again later.');
          setChatStatus('error');
        }
      } else {
        console.error('Unexpected response structure:', response);
        throw new Error('Invalid response from chat initialization');
      }
    } catch (error) {
      console.error('Failed to initialize chat session:', error);
      setChatError('Failed to start the AI configuration chat. Please try again.');
      setChatStatus('error');
    } finally {
      setIsInitializing(false);
    }
  };

  /**
   * Send message to OpenAI chat
   */
  const sendMessage = async (message) => {
    if (!message.trim() || !conversationId || isSending) return;

    try {
      setIsSending(true);
      setChatError(null);
      setChatStatus('sending');

      // Add user message to chat
      const userMessage = {
        id: `user-${Date.now()}`,
        type: 'user',
        content: message.trim(),
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, userMessage]);
      setInputValue('');

      // Send to API
      const response = await apiClient.post('/api/parrots/wizard/step3/message', {
        session_id: formData.session_id,
        conversation_id: conversationId,
        message: message.trim()
      });



      // Handle different response types
      if (response.type === 'finalization') {
        // Finalization response with configuration
        const assistantMessage = {
          id: `assistant-${Date.now()}`,
          type: 'assistant',
          content: response.message,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, assistantMessage]);
        setFinalConfiguration(response.configuration);
        setChatStatus('completed');
      } else {
        // Regular chat response
        const assistantMessage = {
          id: `assistant-${Date.now()}`,
          type: 'assistant',
          content: response.message,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, assistantMessage]);
        setChatStatus('ready');
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      setChatError('Failed to send message. Please try again.');
      setChatStatus('error');
      
      // Remove the user message if sending failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  /**
   * Handle input change
   */
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  /**
   * Handle key press in input
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  /**
   * Handle finalize configuration
   */
  const handleFinalize = async () => {
    if (!finalConfiguration) {
      setChatError('No configuration available to finalize. Please complete the chat first.');
      return;
    }

    try {
      if (onComplete) {
        await onComplete({
          ...formData,
          system_prompt: finalConfiguration.system_prompt,
          first_message: finalConfiguration.first_message,
          conversation_id: conversationId
        });
      }
    } catch (error) {
      console.error('Failed to finalize configuration:', error);
      setChatError('Failed to save configuration. Please try again.');
    }
  };

  /**
   * Retry chat initialization
   */
  const retryInitialization = () => {
    initializeChatSession();
  };

  /**
   * Format timestamp for display
   */
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  /**
   * Check if input contains special commands
   */
  const isSpecialCommand = (text) => {
    const trimmed = text.trim().toLowerCase();
    return trimmed === 'overview' || trimmed === 'finalize';
  };

  return (
    <div className={`step3-ai-config ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${
          theme === 'dark' ? 'bg-teal-900' : 'bg-teal-100'
        }`}>
          <svg className={`w-10 h-10 ${
            theme === 'dark' ? 'text-teal-400' : 'text-teal-600'
          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <h2 className={`text-2xl font-bold mb-2 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          AI Configuration Chat
        </h2>
        <p className={`text-lg ${
          theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
        }`}>
          {isEditing 
            ? 'Refine your parrot\'s behavior through conversation'
            : 'Let\'s customize your parrot\'s behavior through conversation'
          }
        </p>
      </div>

      {/* Chat Interface */}
      <div className={`rounded-lg ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
      }`}>
        {/* Chat Status */}
        {isInitializing && (
          <LoadingSkeleton type="form" count={2} />
        )}

        {chatError && (
          <div className={`p-4 border-l-4 border-red-500 ${
            theme === 'dark' ? 'bg-red-900/20' : 'bg-red-50'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-red-300' : 'text-red-700'
                  }`}>
                    AI Configuration Chat Unavailable
                  </p>
                  <p className={`text-xs mt-1 ${
                    theme === 'dark' ? 'text-red-400' : 'text-red-600'
                  }`}>
                    The AI chat system is temporarily unavailable. Please try again later.
                  </p>
                </div>
              </div>
              <button
                onClick={retryInitialization}
                className={`text-sm px-3 py-1 rounded transition-colors ${
                  theme === 'dark'
                    ? 'text-red-300 hover:bg-red-800 border border-red-600'
                    : 'text-red-700 hover:bg-red-100 border border-red-300'
                }`}
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Messages Area */}
        {!isInitializing && chatStatus !== 'error' && (
          <>
            <div className={`${chatClasses.messages} ${chatClasses.container.split(' ')[2]}`}>
              {messages.map((message) => (
                <FadeTransition key={message.id} show={true}>
                  <div
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-3xl ${message.type === 'user' ? 'ml-12' : 'mr-12'}`}>
                      <ScaleTransition show={true}>
                        <div className={`rounded-lg p-4 shadow-sm ${
                          message.type === 'user'
                            ? 'bg-teal-600 text-white'
                            : theme === 'dark'
                              ? 'bg-gray-700 text-gray-100 border border-gray-600'
                              : 'bg-gray-100 text-gray-900 border border-gray-200'
                        }`}>
                          <div className="whitespace-pre-wrap text-sm leading-relaxed">
                            {message.content}
                          </div>
                          <div className={`text-xs mt-2 opacity-75 ${
                            message.type === 'user' ? 'text-teal-100' : 
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {formatTimestamp(message.timestamp)}
                          </div>
                        </div>
                      </ScaleTransition>
                    </div>
                  </div>
                </FadeTransition>
              ))}
              
              {/* Typing indicator */}
              {isSending && (
                <div className="flex justify-start">
                  <div className="max-w-3xl mr-12">
                    <div className={`rounded-lg p-4 ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <div className="flex items-center space-x-2">
                        <div className="flex space-x-1">
                          <div className={`w-2 h-2 rounded-full animate-pulse ${
                            theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500'
                          }`}></div>
                          <div className={`w-2 h-2 rounded-full animate-pulse delay-75 ${
                            theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500'
                          }`}></div>
                          <div className={`w-2 h-2 rounded-full animate-pulse delay-150 ${
                            theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500'
                          }`}></div>
                        </div>
                        <span className={`text-sm ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          AI is thinking...
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className={`${chatClasses.input} border-t ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              {chatStatus === 'completed' ? (
                <div className={`text-center p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-teal-900/20' : 'bg-teal-50'
                }`}>
                  <p className={`text-sm mb-4 ${
                    theme === 'dark' ? 'text-teal-300' : 'text-teal-700'
                  }`}>
                    Configuration complete! Your parrot's system prompt has been generated.
                  </p>
                  <button
                    onClick={handleFinalize}
                    disabled={isLoading}
                    className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 dark:text-white"
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">Saving...</span>
                      </div>
                    ) : (
                      'Complete Configuration'
                    )}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex space-x-4">
                  <div className="flex-1">
                    <textarea
                      ref={inputRef}
                      value={inputValue}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      placeholder="Type your message here... (try 'Overview' or 'Finalize')"
                      disabled={chatStatus !== 'ready' || isSending}
                      rows={2}
                      className={`w-full px-4 py-3 rounded-lg border resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <div className={`text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {isSpecialCommand(inputValue) && (
                          <span className="text-teal-500 font-medium">
                            Special command detected
                          </span>
                        )}
                      </div>
                      <div className={`text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Press Enter to send, Shift+Enter for new line
                      </div>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={!inputValue.trim() || chatStatus !== 'ready' || isSending}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                      inputValue.trim() && chatStatus === 'ready' && !isSending
                        ? 'bg-teal-600 hover:bg-teal-700 text-white'
                        : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    }`}
                  >
                    {isSending ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    )}
                  </button>
                </form>
              )}
            </div>
          </>
        )}
      </div>

      {/* Help Section */}
      <div className={`mt-6 p-4 rounded-lg ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
      }`}>
        <h4 className={`text-sm font-medium mb-2 ${
          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
        }`}>
          Available Commands:
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            <span className="font-mono bg-teal-100 text-teal-800 px-2 py-1 rounded text-xs">Overview</span>
            <span className="ml-2">Get a summary of your parrot's current behavior</span>
          </div>
          <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            <span className="font-mono bg-teal-100 text-teal-800 px-2 py-1 rounded text-xs">Finalize</span>
            <span className="ml-2">Complete the configuration and save your parrot</span>
          </div>
        </div>
      </div>



      {/* Form Actions */}
      <div className={`${layoutClasses.actions} pt-6`}>
        <button
          type="button"
          onClick={onBack}
          className={`${getResponsiveButtonClasses('md')} border rounded-lg font-medium transition-colors ${touchFriendlyClasses.button} ${
            theme === 'dark'
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700 focus:ring-gray-500'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500'
          } focus:outline-none focus:ring-2 focus:ring-offset-2`}
          disabled={isLoading || isTransitioning}
        >
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back to Voice Settings</span>
          </div>
        </button>
        
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
          {onSaveAndExit && (
            <button
              type="button"
              onClick={onSaveAndExit}
              className={`${getResponsiveButtonClasses('md')} border rounded-lg font-medium transition-colors ${touchFriendlyClasses.button} ${
                theme === 'dark'
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700 focus:ring-gray-500'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500'
              } focus:outline-none focus:ring-2 focus:ring-offset-2`}
              disabled={isLoading || isTransitioning}
            >
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span>Save Without AI Chat</span>
              </div>
            </button>
          )}
          
          {chatStatus === 'completed' && finalConfiguration && (
            <FadeTransition show={true}>
              <button
                onClick={handleFinalize}
                disabled={isLoading || isTransitioning}
                className={`${getResponsiveButtonClasses('md')} rounded-lg font-medium transition-all duration-200 ${touchFriendlyClasses.button} ${
                  !isLoading && !isTransitioning
                    ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 focus:ring-teal-500'
                    : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                } focus:outline-none focus:ring-2 focus:ring-offset-2`}
              >
                {isLoading || isTransitioning ? (
                  <div className="flex items-center">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">
                      {isTransitioning ? 'Transitioning...' : 'Completing...'}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Save With AI Chat</span>
                  </div>
                )}
              </button>
            </FadeTransition>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step3AIConfig;
